import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Box } from "@design-system/Layout/Box";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Modal } from "@design-system/Modal";
import {
    QuestionType,
    ResponseStatus,
    type SupplierTemplateQuestionUpdateOrderInput,
} from "@generated/client/graphql";

import { useTranslation } from "react-i18next";
import { useQuestion, useQuestions } from "../../../../data";
import { keys, RESOURCE_NAME } from "../../i18n";
import { QuestionTypeSelect } from "../QuestionTypeSelect";

import { useSetToast } from "@app/components/Toast";
import {
    canSubmitQuestion,
    isSelectType,
} from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/services";
import { type QuestionDetail } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/types";
import { useUpdateSupplierTemplateQuestionsOrder } from "@app/screens/Supplier/Questions/data";
import { Switch } from "@design-system/Inputs/Switch";
import { Textfield } from "@design-system/Inputs/Textfield";
import { Text } from "@design-system/Typography/Text";
import { useMemo, type FC } from "react";
import { type QuestionModalConfirmArg } from "../../types";
import { DependencyBox } from "./DependencyBox";
import LocaleChipSelect from "./LocaleChipSelect";
import { NameAndDescriptionBlock } from "./NameAndDescriptionBlock";
import { OptionsBlock } from "./OptionsBlock";
import { ProofSelect } from "./ProofSelect";
import {
    constructQuestionInput,
    createQuestionHandlers,
    useQuestionForm,
} from "./hooks";

type QuestionModalProps = {
    title?: string;
    questionId?: string;
    loading: boolean;
    onConfirm: (question: QuestionModalConfirmArg) => void;
    hasKeepOpenOnConfirmOption?: boolean;
    open?: boolean;
    setOpen?: (open: boolean) => void;
    questionnaireId: string;
    withProof?: boolean;
    withNa?: boolean;
    orderToInsert?: number;
};

export const QuestionModal: FC<QuestionModalProps> = ({
    title,
    questionId,
    onConfirm,
    loading,
    hasKeepOpenOnConfirmOption,
    open,
    questionnaireId,
    setOpen,
    withProof = false,
    withNa = false,
    orderToInsert,
}) => {
    const { t } = useTranslation(RESOURCE_NAME);
    const { questions: allQuestions } = useQuestions({
        questionnaireId,
    });
    const { question } = useQuestion(questionnaireId, questionId);
    const {
        questionInput,
        locale,
        setLocale,
        updateField,
        updateLocaleField,
        resetForm,
        availableLocales,
        createMoreQuestions,
        setCreateMoreQuestions,
    } = useQuestionForm(question, questionnaireId, { withProof });

    const { updateQuestionsOrder } = useUpdateSupplierTemplateQuestionsOrder();
    const { setToastError } = useSetToast();

    const filteredQuestions = useMemo(() => {
        return (
            allQuestions?.filter(
                (q) =>
                    [
                        QuestionType.SingleSelect,
                        QuestionType.MultiSelect,
                    ].includes(q.type) && (q?.options?.length ?? 0) > 0,
            ) ?? []
        );
    }, [allQuestions]);

    const showOpts = isSelectType(questionInput.type);

    const disableConfirm = !canSubmitQuestion(
        questionInput,
        locale,
        question as QuestionDetail,
        { withProof },
    );

    const memoizedOptions = useMemo(
        () => questionInput.options,
        [questionInput.options],
    );

    const {
        handleQuestionTypeChange,
        handleProofChange,
        handleOptionsChange,
        handleUnitChange,
        handleNaOptionChange,
        handleIsRequiredOptionChange,
    } = createQuestionHandlers(questionInput, updateField);

    const handleReorderQuestions = () => {
        if (orderToInsert == null) return;

        const questionsToUpdate: SupplierTemplateQuestionUpdateOrderInput[] =
            allQuestions
                .filter(
                    (question) =>
                        question?.order != null &&
                        question?.order >= orderToInsert,
                )
                .map((question) => ({
                    questionId: question.id,
                    order: (question.order ?? 0) + 1,
                }));

        updateQuestionsOrder(questionsToUpdate, {
            onSettled(data) {
                if (
                    data?.updateSupplierTemplateQuestionsOrder?.status ===
                    ResponseStatus.Success
                ) {
                    return;
                }
                setToastError(t(keys.toast_error_message));
            },
        });
    };

    const calculateQuestionOrder = (): number => {
        if (orderToInsert != null) {
            return orderToInsert;
        }

        const maxOrder =
            allQuestions?.reduce((max, q) => {
                return Math.max(max, q.order ?? 0);
            }, 0) ?? 0;

        return maxOrder + 1;
    };

    const handleConfirm = () => {
        if (!questionInput.type) return;

        handleReorderQuestions();
        const questionOrder = calculateQuestionOrder();
        const input = constructQuestionInput(
            questionInput,
            locale,
            questionOrder,
            question,
            questionnaireId,
        );

        onConfirm(input);
        resetForm();
    };

    const handleConfirmAndCreateMore = (checked: boolean) => {
        setCreateMoreQuestions(checked);
    };

    return (
        <Modal open={open} onOpenChange={setOpen}>
            <Modal.Overlay centerContent>
                <Modal.Content className="max-w-[600px] w-full">
                    <Modal.Header withClose>
                        <Modal.Title>
                            {title ?? t(keys.create_questions_modal_title)}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="flex flex-col gap-4 max-h-[600px] overflow-y-auto">
                        <FlexCol gap="6">
                            <LocaleChipSelect
                                locale={locale}
                                setLocale={setLocale}
                                availableLocales={availableLocales}
                            />
                            <FlexCol gap="4">
                                <NameAndDescriptionBlock
                                    questionInput={questionInput}
                                    locale={locale}
                                    questionType={questionInput?.type}
                                    onUpdate={updateField}
                                    onUpdateLocale={updateLocaleField}
                                    className={
                                        showOpts || withProof
                                            ? "rounded-bl-none rounded-b-none"
                                            : ""
                                    }
                                />
                                {filteredQuestions.length > 0 && (
                                    <DependencyBox
                                        question={question}
                                        allQuestions={allQuestions}
                                        dependsOnQuestionId={
                                            questionInput?.dependsOn?.questionId
                                        }
                                        dependsOnQuestionAnswers={
                                            questionInput?.dependsOn
                                                ?.displayIfSelected
                                        }
                                        handleParentQuestionChange={updateField}
                                    />
                                )}
                            </FlexCol>
                            <FlexCol gap="4">
                                <Text className="text-lg font-bold">
                                    {t(
                                        keys.create_questions_modal_answer_label,
                                    )}
                                </Text>

                                <FlexRow gap="4">
                                    <QuestionTypeSelect
                                        onChange={handleQuestionTypeChange}
                                        value={questionInput.type}
                                    />

                                    {questionInput?.type ===
                                        QuestionType.Value && (
                                        <FlexCol className="w-full min-h-10 max-h-10">
                                            <Textfield
                                                required
                                                placeholder={t(
                                                    keys.create_questions_modal_question_unit_label,
                                                )}
                                                onChange={handleUnitChange}
                                                value={
                                                    questionInput?.unit ?? ""
                                                }
                                            />
                                        </FlexCol>
                                    )}

                                    {withProof && !showOpts && (
                                        <ProofSelect
                                            alignDropdown="start"
                                            value={questionInput.proof}
                                            onChange={handleProofChange}
                                        />
                                    )}
                                </FlexRow>

                                {showOpts && (
                                    <OptionsBlock
                                        locale={locale}
                                        opts={memoizedOptions}
                                        onChange={handleOptionsChange}
                                        withProof={withProof}
                                    />
                                )}

                                <FlexCol gap="4" className="items-start">
                                    <Switch
                                        checked={
                                            questionInput.isRequired ?? false
                                        }
                                        onChange={handleIsRequiredOptionChange}
                                        label={t(
                                            keys.create_questions_modal_require_answer_label,
                                        )}
                                        labelPosition="right"
                                        type="default"
                                    />
                                    {withNa && (
                                        <Switch
                                            checked={
                                                questionInput.enableNaOption ??
                                                true
                                            }
                                            onChange={handleNaOptionChange}
                                            label={t(
                                                keys.create_questions_modal_enable_na_option_label,
                                            )}
                                            labelPosition="right"
                                        />
                                    )}
                                </FlexCol>
                            </FlexCol>
                        </FlexCol>
                    </Modal.Body>
                    <Modal.Footer className="items-center">
                        <Modal.CancelCTA>
                            {t(keys.create_questions_modal_cancel)}
                        </Modal.CancelCTA>
                        {hasKeepOpenOnConfirmOption && (
                            <Tooltip>
                                <Tooltip.Trigger asChild>
                                    <Box>
                                        <Switch
                                            checked={createMoreQuestions}
                                            disabled={disableConfirm || loading}
                                            onChange={
                                                handleConfirmAndCreateMore
                                            }
                                            label={t(
                                                keys.create_questions_modal_create_more_questions,
                                            )}
                                        />
                                    </Box>
                                </Tooltip.Trigger>
                                {disableConfirm && (
                                    <Tooltip.Content>
                                        {t(
                                            keys.create_questions_modal_confirm_disabled_tooltip,
                                        )}
                                    </Tooltip.Content>
                                )}
                            </Tooltip>
                        )}
                        <Tooltip>
                            <Tooltip.Trigger asChild>
                                <Box>
                                    <Modal.ConfirmCTA
                                        preventClose={createMoreQuestions}
                                        onClick={handleConfirm}
                                        disabled={disableConfirm || loading}
                                    >
                                        {questionId
                                            ? t(
                                                  keys.update_questions_modal_confirm,
                                              )
                                            : t(
                                                  keys.create_questions_modal_confirm,
                                              )}
                                    </Modal.ConfirmCTA>
                                </Box>
                            </Tooltip.Trigger>
                            {disableConfirm && (
                                <Tooltip.Content>
                                    {t(
                                        keys.create_questions_modal_confirm_disabled_tooltip,
                                    )}
                                </Tooltip.Content>
                            )}
                        </Tooltip>
                    </Modal.Footer>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};
